
import React, { forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost" | "link";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant = "primary",
  size = "md",
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  children,
  disabled,
  type = "button",
  ...props
}, ref) => {
  // Définir les classes de style pour chaque variante (modifiable ici)
  const variantClasses: Record<string, string> = {
    primary: "bg-[#E4002B] text-white shadow-sm hover:bg-[#c7001e]",
    secondary: "bg-[#F5EBD8] text-[#201f1f] font-semibold hover:bg-[#F5EBD8]/80",
    outline: "border-2 border-[#E4002B]/70 bg-transparent text-[#E4002B] hover:bg-[#E4002B]/10",
    ghost: "bg-transparent hover:bg-[#E4002B]/10",
    link: "text-[#E4002B] underline-offset-4 hover:underline bg-transparent"
  };

  // Définir les classes pour les tailles (modifiable ici)
  const sizeClasses: Record<string, string> = {
    sm: "text-xs px-3 py-1.5 h-8",
    md: "text-sm px-4 py-2 h-10",
    lg: "text-base px-5 py-2 h-12"
  };

  return (
    <button
      type={type}
      className={cn(
        // Styles de base
        "relative inline-flex items-center justify-center rounded-md font-medium transition-all",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
        "active:translate-y-[1px]",
        // Gestion de l'opacité en cas de désactivation ou chargement
        disabled || isLoading ? "pointer-events-none opacity-60" : "",
        // Option pour occuper toute la largeur
        fullWidth ? "w-full" : "",
        // Classes de taille
        sizeClasses[size],
        // Classes de variante
        variantClasses[variant],
        // Classes supplémentaires passées en props
        className
      )}
      ref={ref}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </span>
      )}
      
      {leftIcon && <span className="mr-2 flex-shrink-0">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="ml-2 flex-shrink-0">{rightIcon}</span>}
    </button>
  );
});

Button.displayName = "Button";

export default Button;
